import type { HealthPeriod, MemberDto } from "@gladis/shared";
import { FilterSelect } from "@gladis/ui";
import type { ReactNode } from "react";
import { t, useT } from "@gladis/ui/i18n";

/**
 * The two filters — whose money, and over how long.
 *
 * ==========================================================================================
 * THEY MOVED OFF THE CARD, AND THAT REVISES A LAYOUT RULE
 * ==========================================================================================
 *
 * `dashboard-layout.md` required these to "sit on the card itself", and gave a good reason: "a control
 * floating above the panel would read as belonging to the page and would suggest it filtered the cards
 * beside it, which it must not."
 *
 * That held while they governed ONE card. They now govern four pieces — the In, Out and Left figures in
 * the KPI row and the household health waterfall below it — so there is no single card to sit on, and
 * the original rule has nothing left to attach to.
 *
 * The honesty requirement it was protecting does not go away, it moves: the row is labelled with what
 * the filters apply to, and each filtered card states its scope and window in its own hint. Net worth is
 * in the same row and is NOT filtered, which is exactly the confusion the old rule feared — so its card
 * carries a year-ago comparison instead, which reads as its own window, and the waterfall's hint says
 * outright that it follows these controls while the surplus chart's says it does not.
 *
 * ==========================================================================================
 * WHY THE LABELS ARE QUESTIONS
 * ==========================================================================================
 *
 * "Whose money" and "Over how long" rather than "Scope" and "Period" — the words `dashboard.md`'s own
 * table uses. `ExpenseFilters` records what the alternative costs: a control whose accessible name
 * collides with another on the page leaves a screen-reader user hearing the same word twice with
 * nothing to tell them apart, and it surfaced there as a Playwright strict-mode failure rather than as
 * a design note.
 */

/**
 * The control's appearance: THE CARD'S OWN BORDER, AND NO FILL AT ALL.
 *
 * `border-line` is what every surface on this page is drawn with — `StatCard`, `ChartCard`,
 * `FreshnessPanel`, `RecentActivityCard`. These filters sit in the title row directly above those
 * cards, so borrowing `border-input` gave the row a second, slightly different line weight for no
 * reason a reader could name.
 *
 * The FILL IS GONE. It was `bg-input/30`, and its own note argued the translucency was deliberate
 * because "controls render over a live gradient, and an opaque fill cuts a rectangle out of it".
 * That argument runs all the way to its conclusion: no fill at all is the honest version of it. A
 * filter in a page's title row is not a field waiting for input — it is chrome, and the gradient
 * behind the whole page should pass through it.
 *
 * ==========================================================================================
 * `appearance-none` AND A DRAWN CHEVRON, BECAUSE THE NATIVE ONE CANNOT BE MOVED
 * ==========================================================================================
 *
 * A native `<select>`'s arrow is painted by the platform against the border box; `padding-right`
 * shifts the TEXT and leaves the arrow where it was, hard against the edge. There is no CSS that
 * moves it. So the appearance is dropped and the chevron is drawn.
 *
 * ALL OF THAT NOW LIVES IN `FilterSelect` IN `@gladis/ui`, and the argument above is why the move
 * was worth making rather than copying the classes: it is a paragraph of platform knowledge that was
 * true of every filter in the application and written down beside exactly one of them. The Expenses
 * page needed the same control, and a second copy is how two filter rows come to look different.
 *
 * The component draws the chevron INLINE rather than importing one, because `@gladis/ui` depends on
 * no icon set — `Dialog`'s close button made that call first. The path is the shape
 * `ChevronDownIcon` drew, so nothing changed on screen.
 */
export interface HealthFilterState {
  /** `household`, or a current active member's id. */
  scope: string;
  period: HealthPeriod;
}

export function HealthFilters({
  value,
  members,
  onChange,
}: {
  value: HealthFilterState;
  members: readonly MemberDto[];
  onChange: (next: HealthFilterState) => void;
}): ReactNode {
  const t = useT();
  return (
    <>
      <FilterSelect
        id="health-scope"
        label={t("dash.filters.scope")}
        value={value.scope}
        onChange={(scope) => {
          onChange({ ...value, scope });
        }}
        testId="health-scope"
      >
        <option value="household">{t("dash.filters.household")}</option>
        {/*
          CURRENT ACTIVE MEMBERS ONLY, which is what the server accepts and therefore all the control
          may offer. A member who has left keeps any stored split share — household `Out` still
          includes it — and that is exactly why the figures declare that they do not reconcile rather
          than offering a scope nobody can be shown.
        */}
        {members.map((member) => (
          <option key={member.userId} value={member.userId}>
            {member.displayName}
          </option>
        ))}
      </FilterSelect>

      <FilterSelect
        id="health-period"
        label={t("dash.filters.period")}
        value={value.period}
        onChange={(period) => {
          onChange({ ...value, period: period as HealthPeriod });
        }}
        testId="health-period"
      >
        <option value="this-month">{t("dash.filters.thisMonth")}</option>
        <option value="this-year">{t("dash.filters.thisYear")}</option>
      </FilterSelect>
    </>
  );
}

/**
 * WHO the filters currently select, without the period.
 *
 * A section that is "per month" by name takes this rather than `filterSummary`: the expense ranking
 * follows the SCOPE and not the PERIOD, so a card built from the full phrase read "Household, this
 * year, this month". The first version stripped the period back off with a regular expression, which
 * worked in English and would have quietly stopped working in Dutch.
 */
export function filterScope(filters: HealthFilterState, members: readonly MemberDto[]): string {
  return filters.scope === "household"
    ? t("dash.filters.household")
    : (members.find((member) => member.userId === filters.scope)?.displayName ??
        t("dash.filters.member"));
}

/** What the filters currently select, as a phrase for a card's hint. */
export function filterSummary(filters: HealthFilterState, members: readonly MemberDto[]): string {
  const who = filterScope(filters, members);

  return t("dash.filters.summary", {
    who,
    period: t(
      filters.period === "this-month"
        ? "dash.filters.thisMonthLower"
        : "dash.filters.thisYearLower",
    ),
  });
}
